/**
 * CRO test tracking for Salesforce lead payloads.
 *
 * last_page_viewed: the landing page shown after any VWO redirects, immediately
 * before the user entered a form. Never overwritten by form or thank-you URLs.
 *
 * form_flow_viewed: the form-flow path the user actually completed, without
 * step numbers, query parameters, or fragments.
 */

const LAST_PAGE_VIEWED_KEY = "last_page_viewed";
const FORM_FLOW_VIEWED_KEY = "form_flow_viewed";

/**
 * Longest form prefixes first so /compare-2b is not collapsed to /compare-2
 * or /compare.
 */
const FORM_FLOW_PATHS = [
  "/auto-compare",
  "/coverage-check/start",
  "/compare-2-np",
  "/compare-2b",
  "/compare-2c",
  "/compare-2",
  "/compare-3",
  "/compare",
  "/take5",
  "/join-waitlist",
  "/concierge",
] as const;

export type CroTrackingFields = {
  last_page_viewed: string;
  form_flow_viewed: string;
};

export const normalizeCroPath = (path: string): string => {
  if (!path) return "";

  const withoutHash = path.split("#")[0] ?? "";
  const withoutQuery = withoutHash.split("?")[0] ?? "";
  const trimmed = withoutQuery.trim();
  if (!trimmed) return "";
  if (trimmed === "/") return "/";

  return trimmed.replace(/\/+$/, "");
};

export const getFormFlowViewedPath = (path: string): string | null => {
  const normalized = normalizeCroPath(path).toLowerCase();
  if (!normalized) return null;

  if (
    normalized === "/compare-3/thank-you" ||
    normalized.startsWith("/compare-3/thank-you/")
  ) {
    return "/compare-3";
  }

  for (const formPath of FORM_FLOW_PATHS) {
    if (normalized === formPath || normalized.startsWith(`${formPath}/`)) {
      return formPath;
    }
  }

  return null;
};

export const isFormOrThankYouPath = (path: string): boolean =>
  getFormFlowViewedPath(path) !== null;

const getSessionStorage = (): Storage | null => {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export const captureCroTrackingFromPath = (path: string): void => {
  const storage = getSessionStorage();
  if (!storage) return;

  const normalized = normalizeCroPath(path);
  if (!normalized) return;

  const formFlow = getFormFlowViewedPath(normalized);
  if (formFlow) {
    storage.setItem(FORM_FLOW_VIEWED_KEY, formFlow);
    return;
  }

  storage.setItem(LAST_PAGE_VIEWED_KEY, normalized);
};

export const getLastPageViewed = (): string => {
  const storage = getSessionStorage();
  return storage?.getItem(LAST_PAGE_VIEWED_KEY) || "";
};

export const getFormFlowViewed = (): string => {
  const storage = getSessionStorage();
  return storage?.getItem(FORM_FLOW_VIEWED_KEY) || "";
};

export const getCroTrackingForSubmission = (
  currentPath?: string,
): CroTrackingFields => {
  const path =
    currentPath ||
    (typeof window !== "undefined" ? window.location?.pathname : "") ||
    "";

  if (path) {
    captureCroTrackingFromPath(path);
  }

  return {
    last_page_viewed: getLastPageViewed(),
    form_flow_viewed: getFormFlowViewed(),
  };
};
